import { OnrampApiError } from '../client.js';
export function createConnectedWalletController(client, surface, locale, createId = () => crypto.randomUUID(), initialOperationId = null) {
    let snapshot = {
        operation: null,
        busy: false,
        checking: false,
        error: null,
    };
    let idempotencyKey = null;
    let requestedOperationId = initialOperationId;
    let disposed = false;
    let inFlight = false;
    let retryNotBefore = 0;
    let refreshBackoffMs = 7_000;
    const listeners = new Set();
    const completionListeners = new Set();
    let completedOperationId = null;
    const update = (patch) => {
        if (disposed)
            return;
        snapshot = { ...snapshot, ...patch };
        for (const listener of listeners)
            listener();
        if (snapshot.operation?.state === 'complete' &&
            snapshot.operation.operationId !== completedOperationId) {
            completedOperationId = snapshot.operation.operationId;
            for (const listener of completionListeners)
                listener(completedOperationId);
        }
    };
    const provider = () => {
        if (!client.connectedWallet)
            throw new Error('connected_wallet_unavailable');
        return client.connectedWallet;
    };
    const run = async (action, polling = false) => {
        if (inFlight || disposed || (polling && Date.now() < retryNotBefore))
            return;
        inFlight = true;
        update({ busy: true, error: null });
        try {
            await action();
            if (polling) {
                retryNotBefore = 0;
                refreshBackoffMs = 7_000;
            }
        }
        catch (error) {
            if (polling) {
                const retryAfter = error instanceof OnrampApiError ? error.retryAfterMs : 0;
                retryNotBefore = Date.now() + Math.max(refreshBackoffMs, retryAfter);
                refreshBackoffMs = Math.min(refreshBackoffMs * 2, 60_000);
            }
            update({ error: 'unavailable' });
        }
        finally {
            inFlight = false;
            update({ busy: false });
        }
    };
    const start = () => {
        requestedOperationId = null;
        return run(async () => {
            idempotencyKey ??= createId();
            update({ operation: await provider().create({ idempotencyKey, surface, locale }) });
        });
    };
    const resume = (id) => {
        requestedOperationId = id;
        return run(async () => update({ operation: await provider().get(id) }));
    };
    return {
        getSnapshot: () => snapshot,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        start,
        resume,
        retry: () => (requestedOperationId ? resume(requestedOperationId) : start()),
        refresh: () => run(async () => {
            if (snapshot.operation)
                update({ operation: await provider().reconcile(snapshot.operation.operationId) });
        }, true),
        submitted: () => {
            update({ checking: true });
            return run(async () => {
                if (snapshot.operation)
                    update({ operation: await provider().reconcile(snapshot.operation.operationId) });
            }).finally(() => update({ checking: false }));
        },
        providerError() {
            update({ error: 'provider_error' });
        },
        onComplete(listener) {
            completionListeners.add(listener);
            return () => {
                completionListeners.delete(listener);
            };
        },
        dispose() {
            disposed = true;
            listeners.clear();
            completionListeners.clear();
        },
    };
}
